/**
 * Chat Input Component
 * 
 * Text input with send button for chat interface
 */

import React, { useState, KeyboardEvent } from 'react';
import {
  Box,
  TextField,
  IconButton,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  Send as SendIcon
} from '@mui/icons-material';

interface ChatInputProps {
  onSend: (message: string) => void;
  disabled?: boolean;
  placeholder?: string;
  maxLength?: number;
  /**
   * Border radius for the input field. Accepts an MUI spacing multiple
   * (e.g. 3) or an explicit CSS value (e.g. "4px"). Lets each consumer
   * match the radius of the card the input sits inside. Defaults to 3.
   */
  borderRadius?: number | string;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  onSend,
  disabled = false,
  placeholder = 'Ask a question about South African law...',
  maxLength = 1000,
  borderRadius = 3
}) => {
  const [input, setInput] = useState('');

  const handleSend = () => {
    const trimmed = input.trim();
    if (trimmed && !disabled) {
      onSend(trimmed);
      setInput('');
    }
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const charactersRemaining = maxLength - input.length;
  const isNearLimit = charactersRemaining < 100;
  const isOverLimit = charactersRemaining < 0;

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
      <TextField
        fullWidth
        multiline
        maxRows={4}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyPress={handleKeyPress}
        placeholder={placeholder}
        disabled={disabled}
        variant="outlined"
        helperText={
          isNearLimit ? (
            <span style={{ color: isOverLimit ? 'red' : 'orange' }}>
              {charactersRemaining} characters remaining
            </span>
          ) : undefined
        }
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius,
            bgcolor: 'background.paper',
            '& fieldset': {
              borderColor: 'hsl(var(--primary))',
              borderWidth: 2
            },
            '&:hover fieldset': {
              borderColor: 'hsl(var(--primary))',
              borderWidth: 2
            },
            '&.Mui-focused fieldset': {
              borderColor: 'hsl(var(--primary))',
              borderWidth: 2
            }
          }
        }}
      />
      <Tooltip title={disabled ? 'Please wait...' : 'Send message (Enter)'}>
        <span>
          <IconButton
            onClick={handleSend}
            disabled={disabled || !input.trim() || isOverLimit}
            sx={{
              bgcolor: 'hsl(var(--primary))',
              color: 'white',
              width: 48,
              height: 48,
              '&:hover': {
                bgcolor: 'hsl(var(--primary) / 0.9)'
              },
              '&.Mui-disabled': {
                bgcolor: 'action.disabledBackground'
              }
            }}
          >
            {disabled ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              <SendIcon />
            )}
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

